package view

import Glyph
import com.soywiz.kmem.FastIntTransfer
import com.soywiz.kmem.extract
import com.soywiz.korev.MouseButton
import com.soywiz.korge.input.*
import com.soywiz.korge.render.RenderContext
import com.soywiz.korge.view.Container
import com.soywiz.korge.view.Image
import com.soywiz.korge.view.ViewDslMarker
import com.soywiz.korge.view.addTo
import com.soywiz.korge.view.tiles.TileSet
import com.soywiz.korim.bitmap.Bitmap
import com.soywiz.korim.bitmap.Bitmap32
import com.soywiz.korim.bitmap.slice
import com.soywiz.korim.bitmap.sliceWithSize
import com.soywiz.korim.color.RGBA
import com.soywiz.korim.color.RgbaArray
import com.soywiz.korma.geom.RectangleInt
import game.*

class CharMouseEvent(val char: Glyph, mouseEvents: MouseEvents) {
    val mouseX = mouseEvents.currentPosLocal.x
    val mouseY = mouseEvents.currentPosLocal.y
    val col = (mouseEvents.currentPosLocal.x / 8).toInt()
    val row = (mouseEvents.currentPosLocal.y / 8).toInt()
    var shift = mouseEvents.isShiftDown
    val ctrl = mouseEvents.isCtrlDown
    var alt = mouseEvents.isAltDown
    val button = mouseEvents.button.id
    var leftButtonDown = mouseEvents.button == MouseButton.LEFT
    var rightButtonDown = mouseEvents.button == MouseButton.RIGHT
    var middleButtonDown = mouseEvents.button == MouseButton.MIDDLE
}

inline fun Container.terminal(
    font: CgaFont,
    cols: Int,
    rows: Int,
    callback: @ViewDslMarker Terminal.() -> Unit = {}
) = Terminal(font, cols, rows).addTo(this, callback)

open class Terminal(val font: CgaFont, val cols: Int, val rows: Int) : Container() {
    private val bitmap = Bitmap32(cols * CGA_GLYPH_WIDTH, rows * CGA_GLYPH_HEIGHT)
    private val bmpSlice = bitmap.sliceWithSize(0, 0, bitmap.width, bitmap.height)
    private val image = Image(bitmap)

    private val fontPixels = IntArray(8 * 256) { 0 }
    private val terminalPixels = RgbaArray(rows * 8 * cols * 8)
    private val buffer = Array(cols * rows) {
//        val xPos = (it % cols * font.charWidth).toDouble()
//        val yPos = (it / cols * font.charHeight).toDouble()
//
//        val background = image(font[0].glyph) {
//            xy(xPos, yPos)
//            tint = CgaColor.BLACK.rgba
//            smoothing = false
//        }
//
//        val glyph = image(font[0].glyph) {
//            xy(xPos, yPos)
//            tint = CgaColor.LIGHT_GRAY.rgba
//            smoothing = false
//        }

        Glyph(0)
    }

    var onClickChar: ((CharMouseEvent) -> Unit)? = null
    var onMouseEnterChar: ((CharMouseEvent) -> Unit)? = null
    var onMouseExitChar: ((CharMouseEvent) -> Unit)? = null
    var onMouseDownChar: ((CharMouseEvent) -> Unit)? = null
    var onMouseUpChar: ((CharMouseEvent) -> Unit)? = null
    var onDragChar: (suspend (CharMouseEvent) -> Unit)? = null

    private var isDragging = false
    private var dragButton = 0

    private var lastChar: Glyph? = null

    init {
        val pixelsRgba = RgbaArray(8 * 8 * 256)
        font.bitmap.readPixelsUnsafe(0, 0, 256 * 8, 8, pixelsRgba)

        addChild(image)

        for (c in 0..255) {
            for (y in 0 until CGA_GLYPH_HEIGHT) {
                var mask = 0x01
                var row = 0
                for (x in 0 until CGA_GLYPH_WIDTH) {
                    val pixel = pixelsRgba[y * (256 * 8) + c * 8 + x]
                    if (pixel.a != 0) {
                        row = row.or(mask)
                    }

                    mask = mask.shl(1)
                }

                fontPixels[c * 8 + y] = row
            }
        }

        filter = IdentityFilterNoSmoothing

        propagateEvents = false

        scaleY = 2.0

        onOver {
            val char = charAtPoint(it)

            lastChar = char

            onMouseEnterChar?.invoke(CharMouseEvent(char, it))
        }

        onMove {
            val char = charAtPoint(it)

            if (lastChar != null && lastChar != char) {
                onMouseExitChar?.invoke(CharMouseEvent(lastChar!!, it))

                lastChar = char

                onMouseEnterChar?.invoke(CharMouseEvent(char, it))
            }

            if (isDragging) {
                onDragChar?.invoke(CharMouseEvent(char, it))
            }
        }

        onOut {
            onMouseExitChar?.invoke(CharMouseEvent(lastChar!!, it))
        }

        onDown {
            isDragging = true
            dragButton = it.button.id
            val char = charAtPoint(it)

            onMouseDownChar?.invoke(CharMouseEvent(char, it))
            onDragChar?.invoke(CharMouseEvent(char, it))
        }

        onUp {
            isDragging = false
            val char = charAtPoint(it)

            onMouseUpChar?.invoke(CharMouseEvent(char, it))
        }

        onClick {
            val char = charAtPoint(it)
            onClickChar?.invoke(CharMouseEvent(char, it))
        }
    }

    private fun createTileSet(bitmap: Bitmap): TileSet {
        return TileSet(bitmap.slice(), 8, 16)

//        val slices = (0..255).map {
//            bitmap.slice(RectangleInt.fromBounds(it * 8, 0, (it + 1) * 8, 8))
//        }.toList()
//
//        val tileSet = TileSet(slices, 8, 16)
//
//        return tileSet
    }

    private fun charAtPoint(event: MouseEvents) = charAtPoint(event.currentPosLocal.x, event.currentPosLocal.y)
    fun charAtPoint(x: Double, y: Double) = charAtPoint(x.toInt(), y.toInt())

    fun getChar(col: Int, row: Int) = buffer[row * cols + col]

    fun setChar(
        col: Int,
        row: Int,
        char: Int,
        fgColor: CgaColor = CgaColor.LIGHT_GRAY,
        bgColor: CgaColor = CgaColor.BLACK
    ) {
        setChar(col, row, char, fgColor.rgba, bgColor.rgba)
    }

    fun setChar(
        col: Int,
        row: Int,
        char: Int,
        fgColor: RGBA = CgaColor.LIGHT_GRAY.rgba,
        bgColor: RGBA = CgaColor.BLACK.rgba
    ) {
        with(getChar(col, row)) {
            this.char = char
            this.fgColor = fgColor
            this.bgColor = bgColor
        }
    }

    fun charAtPoint(x: Int, y: Int): Glyph {
        val col = (x / 8)
        val row = (y / 8)

        val char = getChar(col, row)

        return char
    }

    fun drawSingleHLine(
        row: Int,
        startCol: Int,
        endCol: Int,
        fgColor: CgaColor = CgaColor.LIGHT_GRAY,
        bgColor: CgaColor = CgaColor.BLACK
    ) {
        drawSingleHLine(row, startCol, endCol, fgColor.rgba, bgColor.rgba)
    }

    fun drawSingleHLine(
        row: Int,
        startCol: Int,
        endCol: Int,
        fgColor: RGBA = CgaColor.LIGHT_GRAY.rgba,
        bgColor: RGBA = CgaColor.BLACK.rgba
    ) {
        for (col in startCol..endCol) {
            setChar(col, row, 0xC4, fgColor, bgColor)
        }
    }

    fun drawSingleVLine(
        col: Int,
        startRow: Int,
        endRow: Int,
        fgColor: CgaColor = CgaColor.LIGHT_GRAY,
        bgColor: CgaColor = CgaColor.BLACK
    ) {
        drawSingleVLine(col, startRow, endRow, fgColor.rgba, bgColor.rgba)
    }

    fun drawSingleVLine(
        col: Int,
        startRow: Int,
        endRow: Int,
        fgColor: RGBA = CgaColor.LIGHT_GRAY.rgba,
        bgColor: RGBA = CgaColor.BLACK.rgba
    ) {
        for (row in startRow..endRow) {
            setChar(col, row, 0xB3, fgColor, bgColor)
        }
    }

    fun drawDoubleHLine(
        row: Int,
        startCol: Int,
        endCol: Int,
        fgColor: CgaColor = CgaColor.LIGHT_GRAY,
        bgColor: CgaColor = CgaColor.BLACK
    ) {
        drawDoubleHLine(row, startCol, endCol, fgColor.rgba, bgColor.rgba)
    }

    fun drawDoubleHLine(
        row: Int,
        startCol: Int,
        endCol: Int,
        fgColor: RGBA = CgaColor.LIGHT_GRAY.rgba,
        bgColor: RGBA = CgaColor.BLACK.rgba
    ) {
        for (col in startCol..endCol) {
            setChar(col, row, 0xCD, fgColor, bgColor)
        }
    }

    fun drawDoubleVLine(
        col: Int,
        startRow: Int,
        endRow: Int,
        fgColor: CgaColor = CgaColor.LIGHT_GRAY,
        bgColor: CgaColor = CgaColor.BLACK
    ) {
        drawDoubleVLine(col, startRow, endRow, fgColor.rgba, bgColor.rgba)
    }

    fun drawDoubleVLine(
        col: Int,
        startRow: Int,
        endRow: Int,
        fgColor: RGBA = CgaColor.LIGHT_GRAY.rgba,
        bgColor: RGBA = CgaColor.BLACK.rgba
    ) {
        for (row in startRow..endRow) {
            setChar(col, row, 0xBA, fgColor, bgColor)
        }
    }

    fun drawSingleBorder(
        startCol: Int,
        startRow: Int,
        endCol: Int,
        endRow: Int,
        fgColor: CgaColor = CgaColor.LIGHT_GRAY,
        bgColor: CgaColor = CgaColor.BLACK
    ) {
        drawSingleBorder(startCol, startRow, endCol, endRow, fgColor.rgba, bgColor.rgba)
    }

    fun drawSingleBorder(
        startCol: Int,
        startRow: Int,
        endCol: Int,
        endRow: Int,
        fgColor: RGBA = CgaColor.LIGHT_GRAY.rgba,
        bgColor: RGBA = CgaColor.BLACK.rgba
    ) {
        drawSingleHLine(startRow, startCol, endCol, fgColor, bgColor)
        drawSingleHLine(endRow, startCol, endCol, fgColor, bgColor)
        drawSingleVLine(startCol, startRow, endRow, fgColor, bgColor)
        drawSingleVLine(endCol, startRow, endRow, fgColor, bgColor)

        setChar(startCol, startRow, 0xDA, fgColor, bgColor)
        setChar(startCol, endRow, 0xC0, fgColor, bgColor)
        setChar(endCol, startRow, 0xBF, fgColor, bgColor)
        setChar(endCol, endRow, 0xD9, fgColor, bgColor)
    }

    fun drawDoubleBorder(
        startCol: Int,
        startRow: Int,
        endCol: Int,
        endRow: Int,
        fgColor: CgaColor,
        bgColor: CgaColor
    ) {
        drawDoubleBorder(startCol, startRow, endCol, endRow, fgColor.rgba, bgColor.rgba)
    }

    fun drawDoubleBorder(
        startCol: Int,
        startRow: Int,
        endCol: Int,
        endRow: Int,
        fgColor: RGBA,
        bgColor: RGBA
    ) {
        drawDoubleHLine(startRow, startCol, endCol, fgColor, bgColor)
        drawDoubleHLine(endRow, startCol, endCol, fgColor, bgColor)
        drawDoubleVLine(startCol, startRow, endRow, fgColor, bgColor)
        drawDoubleVLine(endCol, startRow, endRow, fgColor, bgColor)

        setChar(startCol, startRow, 0xC9, fgColor, bgColor)
        setChar(startCol, endRow, 0xC8, fgColor, bgColor)
        setChar(endCol, startRow, 0xBB, fgColor, bgColor)
        setChar(endCol, endRow, 0xBC, fgColor, bgColor)
    }

    fun fillRect(
        startCol: Int,
        startRow: Int,
        endCol: Int,
        endRow: Int,
        char: Int,
        fgColor: CgaColor,
        bgColor: CgaColor
    ) {
        fillRect(startCol, startRow, endCol, endRow, char, fgColor.rgba, bgColor.rgba)
    }

    fun fillRect(startCol: Int, startRow: Int, endCol: Int, endRow: Int, char: Int, fgColor: RGBA, bgColor: RGBA) {
        for (row in startRow..endRow) {
            for (col in startCol..endCol) {
                setChar(col, row, char, fgColor, bgColor)
            }
        }
    }

    fun drawText(
        message: String,
        startCol: Int,
        startRow: Int,
        endCol: Int,
        rowHeight: Int,
        fgColor: CgaColor,
        bgColor: CgaColor
    ) {
        val words = message.split(" ")

        var wordCol = 0
        var wordRow = 0
        val lineLength = endCol - startCol

        for (word in words) {
            if (wordCol != 0 && word.length + wordCol >= lineLength) {
                wordCol = 0
                wordRow += rowHeight
            }

            drawString(
                wordCol + startCol,
                wordRow + startRow,
                word,
                fgColor,
                bgColor
            )

            wordCol += word.length + 1  // +1 to accommodate space...
        }
    }

    fun drawString(col: Int, row: Int, text: String, fgColor: CgaColor, bgColor: CgaColor) {
        drawString(col, row, text, fgColor.rgba, bgColor.rgba)
    }

    fun drawString(col: Int, row: Int, text: String, fgColor: RGBA, bgColor: RGBA) {
        var currentCol = col
        for (char in text) {
            setChar(currentCol, row, char.code, fgColor, bgColor)
            currentCol++
        }
    }

    fun drawStringCentered(
        left: Int,
        top: Int,
        right: Int,
        text: String,
        fgColor: CgaColor,
        bgColor: CgaColor,
        fillChar: Int = 32
    ) {
        drawStringCentered(left, top, right, text, fgColor.rgba, bgColor.rgba, fillChar)
    }

    fun drawStringCentered(
        left: Int,
        top: Int,
        right: Int,
        text: String,
        fgColor: RGBA,
        bgColor: RGBA,
        fillChar: Int = 32
    ) {
        fillRect(left, top, right, top, fillChar, fgColor, bgColor)
        val mid = (right - left + 1) / 2
        val textLeft = left + mid - text.length / 2
        drawString(textLeft, top, text, fgColor, bgColor)
    }

    fun clear(color: CgaColor) {
        fillRect(0, 0, cols - 1, rows - 1, 0, color, CgaColor.WHITE)
        this.visible
    }

    fun copy(
        terminal: Terminal,
        colOffset: Int,
        rowOffset: Int,
        startCol: Int,
        startRow: Int,
        width: Int,
        height: Int
    ) {
        for (y in 0 until height) {
            for (x in 0 until width) {
                val destCol = x + colOffset
                val destRow = y + rowOffset

                val tile = terminal.getChar(startCol + x, startRow + y)

                setChar(destCol, destRow, tile.char, tile.fgColor, tile.bgColor)
            }
        }
    }

    override fun renderInternal(ctx: RenderContext) {
        renderTerminal()

        ctx.getTex(bitmap).update(bitmap)

        super.renderInternal(ctx)
    }

    private val pixelsTransfer = FastIntTransfer()

    private fun renderTerminal() {
        val pixels = bitmap.data

        pixelsTransfer.use(pixels.ints) { pixelsFast ->
            for (row in 0 until rows) {
                for (col in 0 until cols) {
                    val glyph = buffer[row * cols + col]
                    val glyphChar = glyph.char
                    val bgColorInt = glyph.bgColor.value
                    val fgColorInt = glyph.fgColor.value
                    for (y in 0 until CGA_GLYPH_HEIGHT) {
                        val rowIndex = glyphChar * 8 + y
                        val pixelRow = fontPixels[rowIndex]
                        val index = (row * 8 + y) * cols * 8 + col * 8
                        for (x in 0 until CGA_GLYPH_WIDTH) {
                            pixelsFast[index + x] = if (pixelRow.extract(x)) fgColorInt else bgColorInt
                        }
                    }
                }
            }
        }
    }
}
